import { useEffect, useRef } from 'react';
import type { PendingUserMessage } from './AgentStreamDataContext';
import type { AgentCommandStatusResponse } from '@reflex/shared';

/**
 * Per-request server-side hold. The server caps this (25s); one held
 * request at a time per command keeps a minute-long watch to a handful
 * of round trips instead of a poll per second.
 */
const WATCH_REQUEST_WAIT_MS = 25_000;
/**
 * Pause between reads after a transport error or a non-terminal answer.
 * The server may answer a held read at once (its hold quota is bounded),
 * so this is what keeps a watcher from spinning on the request budget.
 */
const WATCH_RETRY_DELAY_MS = 2_000;

export type CommandOutcome =
  | { status: 'sent' }
  | { status: 'failed'; failure: NonNullable<AgentCommandStatusResponse['failure']> };

export interface CommandStatusReader {
  (
    agentId: string,
    commandId: string,
    waitMs: number,
    signal: AbortSignal,
  ): Promise<AgentCommandStatusResponse>;
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal.aborted) return resolve();
    const t = setTimeout(done, ms);
    function done() {
      signal.removeEventListener('abort', done);
      clearTimeout(t);
      resolve();
    }
    signal.addEventListener('abort', done, { once: true });
  });
}

/**
 * Watch the delivery outcome of every `'sending'` pending entry that
 * carries a `commandId` (a send the server accepted with 202 because its
 * outcome outlasted the verb's bounded wait — a sleeping agent's wake).
 *
 * One watcher per entry, started when the entry first appears and aborted
 * when it leaves the `sending` state, disappears, or the provider unmounts.
 * Each watcher long-polls the command status until it reaches `sent` or
 * `failed`, or the entry's `createdAt + deadlineMs` passes (the echo
 * watchdog owns that outcome). `onOutcome` fires at most once per entry.
 *
 * A `sent` outcome is informational — the stream echo still resolves the
 * bubble — but `failed` is the only way the client learns a slow send was
 * refused, so it is surfaced immediately with the server's reason instead
 * of waiting out the echo deadline as "unconfirmed".
 */
export function useCommandOutcomeWatch(
  pendingMessages: Pick<PendingUserMessage, 'clientId' | 'commandId' | 'createdAt' | 'status'>[],
  agentId: string,
  readStatus: CommandStatusReader,
  onOutcome: (clientId: string, outcome: CommandOutcome) => void,
  deadlineMs: number,
): void {
  const watchers = useRef(new Map<string, AbortController>());
  // Latest callbacks, read by long-lived watchers without restarting them
  // on every render. Updated in an effect, never during render.
  const onOutcomeRef = useRef(onOutcome);
  const readStatusRef = useRef(readStatus);
  useEffect(() => {
    onOutcomeRef.current = onOutcome;
    readStatusRef.current = readStatus;
  }, [onOutcome, readStatus]);

  // Keyed by entry AND command: a retried bubble keeps its clientId but
  // gets a new commandId, which must start a fresh watch. An entry stays in
  // the map after its watch finishes (outcome reported, 404, or deadline)
  // until the bubble leaves `sending`, so a finished watch is never
  // restarted by an unrelated pending-state change.
  useEffect(() => {
    const live = new Set<string>();
    for (const p of pendingMessages) {
      if (p.status !== 'sending' || !p.commandId) continue;
      const key = `${p.clientId}:${p.commandId}`;
      live.add(key);
      if (watchers.current.has(key)) continue;
      const controller = new AbortController();
      watchers.current.set(key, controller);
      void watch(p.clientId, p.commandId, p.createdAt, controller.signal);
    }
    for (const [key, controller] of watchers.current) {
      if (live.has(key)) continue;
      controller.abort();
      watchers.current.delete(key);
    }

    async function watch(
      clientId: string,
      commandId: string,
      createdAt: number,
      signal: AbortSignal,
    ) {
      while (!signal.aborted) {
        const remaining = createdAt + deadlineMs - Date.now();
        if (remaining <= 0) return;
        let data: AgentCommandStatusResponse;
        try {
          data = await readStatusRef.current(
            agentId,
            commandId,
            Math.min(WATCH_REQUEST_WAIT_MS, remaining),
            signal,
          );
        } catch (err) {
          if (signal.aborted) return;
          // A missing command (404) has no outcome to learn; anything else
          // is transport trouble worth retrying until the deadline.
          if (isNotFound(err)) return;
          await sleep(WATCH_RETRY_DELAY_MS, signal);
          continue;
        }
        if (signal.aborted) return;
        if (data.status === 'failed') {
          onOutcomeRef.current(clientId, {
            status: 'failed',
            failure: data.failure ?? { code: 'unknown' },
          });
          return;
        }
        if (data.status === 'sent') {
          onOutcomeRef.current(clientId, { status: 'sent' });
          return;
        }
        await sleep(WATCH_RETRY_DELAY_MS, signal);
      }
    }
  }, [pendingMessages, agentId, deadlineMs]);

  useEffect(() => {
    const map = watchers.current;
    return () => {
      for (const controller of map.values()) controller.abort();
      map.clear();
    };
  }, []);
}

function isNotFound(err: unknown): boolean {
  return (
    typeof err === 'object' &&
    err !== null &&
    ((err as { status?: unknown }).status === 404 ||
      (err as { statusCode?: unknown }).statusCode === 404)
  );
}
